import { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Fullscreen image viewer for card attachments.
 * Steps through `images` (already ordered) with keyboard ←/→/Esc,
 * on-screen arrows, and a backdrop click to close.
 */
export default function AttachmentLightbox({ images = [], index = 0, currentUserId, onClose, onNavigate }) {
    const { t } = useTranslation()
    const total = images.length
    const att = images[index]

    const go = useCallback((delta) => {
        const next = index + delta
        if (next < 0 || next >= total) return
        onNavigate?.(next)
    }, [index, total, onNavigate])

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose?.()
            else if (e.key === 'ArrowLeft') go(-1)
            else if (e.key === 'ArrowRight') go(1)
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [go, onClose])

    if (!att) return null

    const closeFromBackdrop = (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (event.target === event.currentTarget) onClose?.()
    }

    const uploader = att.profiles?.full_name || t('card.attachments.unknownUser')
    const isMine = att.created_by && att.created_by === currentUserId
    const byline = isMine
        ? t('card.attachments.uploadedByYou', { name: uploader })
        : t('card.attachments.uploadedBy', { name: uploader })

    return createPortal(
        <div
            className="att-lb-overlay"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={closeFromBackdrop}
            role="dialog"
            aria-modal="true"
        >
            <button
                className="att-lb-close"
                onClick={(event) => { event.stopPropagation(); onClose?.() }}
                title={t('common.close')}
                aria-label={t('common.close')}
            >
                <X size={22} />
            </button>

            {index > 0 && (
                <button
                    className="att-lb-nav att-lb-prev"
                    onClick={(e) => { e.stopPropagation(); go(-1) }}
                    title={t('card.attachments.prev')}
                    aria-label={t('card.attachments.prev')}
                >
                    <ChevronLeft size={28} />
                </button>
            )}

            <div className="att-lb-stage" onClick={(e) => e.stopPropagation()}>
                <img className="att-lb-img" src={att.url} alt={att.name} />
                <div className="att-lb-caption">
                    <div className="att-lb-name">{att.name}</div>
                    <div className="att-lb-meta">
                        <span>{byline}</span>
                        <span className="att-lb-counter">{index + 1} / {total}</span>
                        <a
                            className="att-lb-download"
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Download size={14} /> {t('card.attachments.download')}
                        </a>
                    </div>
                </div>
            </div>

            {index < total - 1 && (
                <button
                    className="att-lb-nav att-lb-next"
                    onClick={(e) => { e.stopPropagation(); go(1) }}
                    title={t('card.attachments.next')}
                    aria-label={t('card.attachments.next')}
                >
                    <ChevronRight size={28} />
                </button>
            )}
        </div>,
        document.body
    )
}
